import { MacroItem, TemplateMacroEditProps } from '@/template/interface'
import { Guid } from '@/utils/guid'
import { getFormatMessage } from '@/utils/utils'
import {
  BorderOutlined,
  PauseOutlined,
  PlaySquareOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
  StepForwardOutlined
} from '@ant-design/icons'
import { Button, Col, Form, Input, Modal, Row, Space } from 'antd'
import React, { ReactNode, useEffect, useState } from 'react'
import { TemplateMacroDocument } from '../docs'
import { TemplateMacroEditor } from '../editor'
import { TemplateMacroVariable } from '../variable'

export const TemplateMacroEdit: React.FC<TemplateMacroEditProps> = (
  props: TemplateMacroEditProps
) => {
  const defaultCode =
    props.item?.script ||
    `function main() {
  // write some macro code here

  return 0;
}
main();`
  const [form] = Form.useForm<MacroItem>()
  const [code, setCode] = useState<string>(defaultCode)
  const [docVisible, setDocVisible] = useState<boolean>(false)
  const formatMessage = getFormatMessage()

  const handleEditorChange = (value: string) => {
    setCode(value)
    props.onStateChange?.(true)
  }

  const handleSaveClick = (close?: boolean) => {
    form
      .validateFields()
      .then((values: MacroItem) => {
        const merged = Object.assign({ ...props.item }, values)
        if (!merged.id) {
          merged.id = Guid.raw()
        }
        if (!merged.variableIds) {
          merged.variableIds = []
        }
        merged.script = code
        props.onSave?.(merged, close)
      })
      .catch((info) => {
        console.log('Validate Failed:', info)
      })
  }

  const getStatusButtons = (status): ReactNode[] => {
    let btnRunIcon: ReactNode = <PlaySquareOutlined />
    let btnRunDisabled = false
    let btnStepDisabled = false
    let btnStopVisible = false
    switch (status) {
      // NOT_PARSED
      case 0:
        btnStopVisible = false
        btnRunIcon = <PlaySquareOutlined />
        btnStepDisabled = false
        btnRunDisabled = false
        break
      // PAUSED
      case 1:
        btnStopVisible = true
        btnRunIcon = <PlaySquareOutlined />
        break
      // RUNNING
      case 2:
        btnStopVisible = true
        btnRunIcon = <PauseOutlined />
        btnStepDisabled = true
        break
    }

    return [
      <Button
        key='macroStop'
        onClick={() => props.onStop?.()}
        type='primary'
        icon={<BorderOutlined />}
        style={{ visibility: btnStopVisible ? 'visible' : 'hidden' }}
      />,
      <Button
        key='macroRun'
        onClick={() =>
          props.onRun?.(code, props.variables || [], props.plcs || [])
        }
        type='primary'
        disabled={btnRunDisabled}
        icon={btnRunIcon}
      />,
      <Button
        key='macroStep'
        onClick={() =>
          props.onStep?.(code, props.variables || [], props.plcs || [])
        }
        type='primary'
        disabled={btnStepDisabled}
        icon={<StepForwardOutlined />}
      />
    ]
  }

  useEffect(() => {
    if (props.item) {
      form.setFieldsValue(props.item)
    }
  }, [])

  useEffect(() => {
    if (props.formResult) {
      handleSaveClick(true)
    }
  }, [props.formResult])

  return (
    <div>
      <Row style={{ marginBottom: 10 }}>
        <Col span='13'>
          <Form form={form} layout='inline'>
            <Form.Item
              label={formatMessage({ id: 'app.template.macro.edit.name' })}
              name='name'
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'app.template.macro.edit.name.message'
                  })
                }
              ]}
            >
              <Input onChange={() => props.onStateChange?.(true)} />
            </Form.Item>
            <Form.Item
              label={formatMessage({
                id: 'app.template.macro.edit.description'
              })}
              name='description'
            >
              <Input onChange={() => props.onStateChange?.(true)} />
            </Form.Item>
          </Form>
        </Col>
        <Col span='11'>
          <Space>
            {getStatusButtons(props.status)}
            <Button
              onClick={() => handleSaveClick(true)}
              type='primary'
              disabled={!props.saving}
              icon={<SaveOutlined />}
            >
              {formatMessage({ id: 'app.template.macro.edit.saveClose' })}
            </Button>
            <Button
              onClick={() => handleSaveClick()}
              type='primary'
              disabled={!props.saving}
              icon={<SaveOutlined />}
            >
              {formatMessage({ id: 'app.template.macro.edit.save' })}
            </Button>

            <Button
              onClick={() => setDocVisible(true)}
              type='primary'
              icon={<QuestionCircleOutlined />}
            >
              {formatMessage({ id: 'app.template.macro.edit.help' })}
            </Button>
          </Space>
        </Col>
      </Row>
      <Row>
        <Col span={6}>
          {/** 脚本不支持按位索引，禁用所有按位索引的点位(jira-FBOX-3726) */}
          <TemplateMacroVariable
            {...props}
            variables={props.variables}
            item={props.item}
            onItemChange={props.onItemChange}
          />
        </Col>
        <Col span={18}>
          <TemplateMacroEditor
            {...props}
            onChange={handleEditorChange}
            defaultValue={code}
          />
        </Col>
      </Row>

      <Modal
        visible={docVisible}
        onCancel={() => setDocVisible(false)}
        title={formatMessage({ id: 'app.template.macro.edit.docTitle' })}
        width={1000}
        okText={formatMessage({ id: 'app.modal.ok' })}
        cancelText={formatMessage({ id: 'app.modal.cancel' })}
        maskClosable={false}
        destroyOnClose={true}
      >
        <TemplateMacroDocument />
      </Modal>
    </div>
  )
}
